import React, { useEffect, useState } from 'react';
import { API_URLS } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const FAQPageFull = () => {
  const [categories, setCategories] = useState([]);
  const iconMap = {
    HelpCircle,
  };
  const [open, setOpen] = useState({});
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(API_URLS.FAQ);
        if (!res.ok) return;
        const data = await res.json();
        const cats = (data.categories || []).map(cat => ({ id: cat.id, title: cat.title, color: cat.color || 'from-blue-500 to-cyan-500', icon: cat.icon || 'HelpCircle', questions: [] }));
        const byId = Object.fromEntries(cats.map(c => [c.id, c]));
        (data.questions || []).forEach(q => { if (byId[q.category_id]) byId[q.category_id].questions.push({ question: q.question, answer: q.answer }); });
        const arr = Object.values(byId);
        setCategories(arr);
        if (arr.length > 0) setSelectedCatId(arr[0].id);
      } catch(e) {}
    };
    load();
  }, []);

  const toggle = (cid, idx) => {
    const key = `${cid}-${idx}`;
    setOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section 
        className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur px-6 py-3 rounded-full mb-6">
            <HelpCircle className="w-5 h-5" />
            <span>Sıkça Sorulan Sorular</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">Tüm Sorular</h1>
          <p className="text-purple-200 mt-4 max-w-2xl mx-auto">Kategorilere göre tüm SSS içeriklerini burada bulabilirsiniz.</p>
        </div>
      </section>

      <div 
        className="container mx-auto px-6 py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Desktop Only */}
          <aside className="hidden lg:block lg:col-span-1">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Kategoriler</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCatId(cat.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${selectedCatId===cat.id ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{cat.title}</span>
                      <Badge variant="secondary" className="text-xs">{cat.questions.length}</Badge>
                    </div>
                  </button>
                ))}
                {categories.length===0 && (
                  <p className="text-sm text-gray-500">Henüz kategori bulunmuyor.</p>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Mobile Filters - Wrap Layout */}
          <div className="lg:hidden space-y-4 mb-6">
            {/* Mobile Categories */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1 flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                Kategoriler
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCatId(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCatId === cat.id
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{cat.title}</span>
                    <Badge variant="secondary" className={selectedCatId === cat.id ? 'bg-white text-purple-600' : 'bg-white'}>
                      {cat.questions.length}
                    </Badge>
                  </button>
                ))}
                {categories.length === 0 && (
                  <p className="text-sm text-gray-500 px-1">Henüz kategori bulunmuyor.</p>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <section className="lg:col-span-3 space-y-6">
            {categories.filter(c=>c.id===selectedCatId).map(cat => (
              <Card key={cat.id} className="shadow-lg">
                <CardHeader className={`bg-gradient-to-r ${cat.color} text-white rounded-t-lg`}>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {(() => {
                        const IconComp = iconMap[cat.icon] || HelpCircle;
                        return <IconComp className="w-5 h-5" />;
                      })()}
                      {cat.title}
                    </span>
                    <Badge className="bg-white/20">{cat.questions.length} soru</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {cat.questions.map((q, idx) => {
                    const key = `${cat.id}-${idx}`;
                    const isOpen = !!open[key];
                    return (
                      <div key={idx} className="border-b border-gray-200 last:border-b-0">
                        <button onClick={() => toggle(cat.id, idx)} className="w-full px-6 py-4 text-left hover:bg-gray-50 flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900 pr-4">{q.question}</h3>
                          {isOpen ? <ChevronUp className="w-5 h-5 text-purple-600"/> : <ChevronDown className="w-5 h-5 text-gray-400"/>}
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4 text-gray-700">{q.answer}</div>
                        )}
                      </div>
                    );
                  })}
                  {cat.questions.length===0 && (
                    <div className="p-6 text-gray-600">Bu kategoride soru bulunmuyor.</div>
                  )}
                </CardContent>
              </Card>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};

export default FAQPageFull;


